import {
  faArrowDownToLine,
  faArrowUpFromLine,
} from "@fortawesome/pro-regular-svg-icons";
import saveAs from "file-saver";
import { observer } from "mobx-react-lite";
import { useRef } from "react";
import Dropzone, { DropzoneRef } from "react-dropzone";
import { v4 as uuidV4 } from "uuid";

import { informPreparingDownload } from "src/api/downloadUtils";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { BeMappedNode } from "src/constants/NodeDataTypes";
import {
  useExportDecisionTable,
  useImportDecisionTable,
} from "src/decisionTableNode/queries";
import { toastActions } from "src/design-system/Toast/utils";
import { useGraphStore } from "src/store/StoreProvider";
import { errorMessage } from "src/utils/stringUtils";

const downloadTemplate = () => {
  const templateData =
    "CONDITION,RESULT,NAME\ndata.condition,data.result,Name\nis any,result,Case 1\n,None,Fallback case";
  const blob = new Blob([templateData], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "decision_table_template.csv";
  link.click();
  URL.revokeObjectURL(url);
  toastActions.success({
    title: "Downloaded template successfully",
  });
};

export const DecisionTableLogicTitle: React.FC<{
  flowVersionId: string;
  selectedNode: BeMappedNode;
}> = observer(({ flowVersionId, selectedNode }) => {
  const { mutateAsync: importDecisionTable, isPending: isUploading } =
    useImportDecisionTable();
  const { mutateAsync: exportDecisionTable, isPending: isExporting } =
    useExportDecisionTable();

  const dropzoneRef = useRef<DropzoneRef>(null);
  const { updateNode } = useGraphStore();

  const openDropzone = () => {
    dropzoneRef.current?.open();
  };

  const downloadDecisionTableCsv = async (
    flowVersionId: string,
    nodeId: string,
  ) => {
    const toastId = uuidV4();
    informPreparingDownload(toastId);
    try {
      const response = await exportDecisionTable({
        flowVersionId,
        nodeId,
      });

      saveAs(response, `${selectedNode.data.label}.csv`);
      toastActions.success({
        title: "CSV downloaded successfully",
        id: toastId,
      });
    } catch (error) {
      toastActions.failure({
        title: "Failed to download CSV",
        description: errorMessage(error),
        id: toastId,
      });
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0 && selectedNode?.id) {
      const file = acceptedFiles[0];
      const toastId = toastActions.loading({
        title: `Importing ${file.name}`,
      });
      try {
        const response = await importDecisionTable({
          flowVersionId,
          nodeId: selectedNode.id,
          file,
        });
        const decisionTableNodeMeta = response.data;

        updateNode({
          nodeId: selectedNode.id,
          newData: {
            predicate_fields: decisionTableNodeMeta.predicate_fields,
            effect_fields: decisionTableNodeMeta.effect_fields,
            cases: decisionTableNodeMeta.cases,
            fallback: decisionTableNodeMeta.fallback,
          },
        });

        toastActions.success({
          title: "Imported successfully",
          id: toastId,
        });
      } catch (error) {
        toastActions.failure({
          title: "Error importing",
          description: errorMessage(error),
          id: toastId,
        });
      }
    }
  };

  const optionElements = [
    {
      key: "Import table",
      icon: faArrowUpFromLine,
      action: openDropzone,
      disabled: isUploading || isExporting,
    },
    {
      key: "Download CSV template",
      icon: faArrowDownToLine,
      action: downloadTemplate,
      disabled: isUploading || isExporting,
    },
    {
      key: "Export table as CSV",
      icon: faArrowDownToLine,
      action: () => {
        downloadDecisionTableCsv(flowVersionId, selectedNode.id);
      },
      disabled: isUploading || isExporting,
    },
  ];

  return (
    <div className="flex items-center gap-2">
      Logic
      <EllipsisOptionsDropdown
        dataLoc="table-options-dropdown"
        elements={optionElements}
      />
      <div className="hidden">
        <Dropzone
          ref={dropzoneRef}
          accept={{
            "text/csv": [".csv"],
          }}
          maxFiles={1}
          noClick={true}
          noKeyboard={true}
          onDrop={onDrop}
        >
          {({ getRootProps, getInputProps }) => (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
            </div>
          )}
        </Dropzone>
      </div>
    </div>
  );
});
